import { Autocomplete, Avatar, Box, Button, Chip, Divider, IconButton, Stack, TextField, Typography } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useEffect, useMemo, useState } from "react";
import { useChatPdVContext } from "../../../../contexts/atendimento/ChatPdVContext";
import { useMoeda } from "../../../../../core/hooks/useMoeda";
import { useSnackbar } from "../../../../../core/contexts/SnackbarContext";
import { RealInput } from "../../../../../core/components/RealInput";
import { ISabor } from "../../../../../produtos/interfaces/ISabor";
import { IBorda } from "../../../../../produtos/interfaces/IBorda";
import { IMassa } from "../../../../../produtos/interfaces/IMassa";
import { IAdicionalPizza } from "../../../../../produtos/interfaces/IAdicionalPizza";
import { IProdutoPedido } from "../../../../interface/IProdutoPedido";
import { IProduto } from "../../../../../produtos/interfaces/IProduto";
import { acaoPrincipal, contorno, cores, limitarLinhas, texto } from "../../identidade";

export const ChatPdVFormPizza = () => {
    const {
        produtoFormulario,
        voltarTela,
        adicionarProduto,
        editarProduto,
    } = useChatPdVContext();

    const produto = produtoFormulario as IProduto | IProdutoPedido | undefined;
    const produtoPedido = produtoFormulario as IProdutoPedido | undefined;

    // uuidProdutoReferencia só existe em itens do carrinho (IProdutoPedido),
    // não em produtos do catálogo (IProduto)
    const isEdicao = !!(produtoPedido as any)?.uuidProdutoReferencia;

    const snackbar = useSnackbar();
    const { real } = useMoeda();

    const [sabores, setSabores] = useState<ISabor[]>([]);
    const [massa, setMassa] = useState<IMassa | undefined | null>(null);
    const [borda, setBorda] = useState<IBorda | undefined | null>(null);
    const [observacao, setObservacao] = useState<string | undefined>(undefined);
    const [valorAdicional, setValorAdicional] = useState<any>(undefined);
    const [adicionaisSelecionados, setAdicionaisSelecionados] = useState<IAdicionalPizza[]>([]);

    useEffect(() => {
        if (!isEdicao) return;
        if (produtoPedido?.sabores) setSabores(produtoPedido.sabores as ISabor[]);
        if (produtoPedido?.borda) setBorda(produtoPedido.borda as IBorda);
        if (produtoPedido?.massa) setMassa(produtoPedido.massa as IMassa);
        if (produtoPedido?.observacao) setObservacao(produtoPedido.observacao);
        if (produtoPedido?.valorAdicional) setValorAdicional(produtoPedido.valorAdicional);
        if (produtoPedido?.adicionaisSelecionados) setAdicionaisSelecionados(produtoPedido.adicionaisSelecionados);
    }, []);

    const valorTotalSabor = useMemo(() => {
        if (!produto) return 0;

        if ((produto as IProduto).configuracao?.modeloPrecificacao === "precoDividido") {
            let total = 0;
            sabores.forEach(sabor => { total += sabor.preco; });
            return Math.ceil(total / sabores.length);
        }

        if ((produto as IProduto).configuracao?.modeloPrecificacao === "maiorPreco") {
            if (sabores.length === 0) return 0;
            return sabores.reduce((maior, atual) => maior.preco > atual.preco ? maior : atual).preco;
        }

        if ((produto as IProduto).configuracao?.modeloPrecificacao === "precoMisto") {
            if (sabores.length > 2) {
                return sabores.reduce((maior, atual) => maior.preco > atual.preco ? maior : atual).preco;
            } else {
                let total = 0;
                sabores.forEach(sabor => { total += sabor.preco; });
                return Math.ceil(total / sabores.length);
            }
        }

        return 0;
    }, [sabores, produto]);

    const valorTotalAdicionais = useMemo(() => {
        return adicionaisSelecionados.reduce((acc, a) => acc + a.preco, 0);
    }, [adicionaisSelecionados]);

    const valorTotal = useMemo(() => {
        return (valorTotalSabor ?? 0) + (borda?.preco ?? 0) + (massa?.preco ?? 0) + (valorAdicional ?? 0) + valorTotalAdicionais;
    }, [valorTotalSabor, borda, massa, valorAdicional, valorTotalAdicionais]);

    const limiteAdicionaisAtingido = useMemo(() => {
        const limite = (produto as IProduto)?.configuracao?.maxAdicionais;
        return limite !== undefined && adicionaisSelecionados.length >= limite;
    }, [adicionaisSelecionados, produto]);

    const handleToggleAdicional = (adicional: IAdicionalPizza) => {
        const jaExiste = adicionaisSelecionados.some(a => a.uuid === adicional.uuid);
        if (jaExiste) {
            setAdicionaisSelecionados(prev => prev.filter(a => a.uuid !== adicional.uuid));
        } else {
            if (limiteAdicionaisAtingido) {
                snackbar(`Limite de ${(produto as IProduto).configuracao?.maxAdicionais} adicionais atingido`, { severity: "warning" });
                return;
            }
            setAdicionaisSelecionados(prev => [...prev, adicional]);
        }
    };

    const handleSaboresChange = (_event: React.SyntheticEvent, value: ISabor[]) => {
        if (produto && (produto as IProduto).quantidadeSabores) {
            if (value.length > (produto as IProduto).quantidadeSabores!) {
                return snackbar(`Você já selecionou os ${(produto as IProduto).quantidadeSabores} sabores`, { severity: "error" });
            }
        }
        setSabores(value);
    };

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!sabores.length) {
            snackbar("Sabores inválidos", { severity: "error" });
            return;
        }

        if (!produto) return;

        if ((produto as IProduto).configuracao?.massaObrigatoria && !massa) {
            snackbar("Massa é obrigatória", { severity: "error" });
            return;
        }

        if ((produto as IProduto).configuracao?.bordaObrigatoria && !borda) {
            snackbar("Borda é obrigatória", { severity: "error" });
            return;
        }

        const dadosProduto: IProdutoPedido = {
            uuidProdutoReferencia: produto.uuid,
            valorAdicional,
            ...produto,
            observacao,
            sabores,
            massa: massa ?? undefined,
            borda: borda ?? undefined,
            adicionaisSelecionados: adicionaisSelecionados.length ? adicionaisSelecionados : undefined,
        };

        if (isEdicao && produtoPedido?.uuid) {
            dadosProduto.uuid = produtoPedido.uuid;
            editarProduto(dadosProduto);
        } else {
            adicionarProduto(dadosProduto);
        }

        voltarTela();
    };

    if (!produto) return null;

    const produtoIProduto = produto as IProduto;

    return (
        <Box
            sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                height: '100%',
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.75,
                    py: 1.5,
                    borderBottom: theme => contorno(theme, 0.09),
                    flexShrink: 0,
                }}
            >
                <IconButton onClick={voltarTela} size="small">
                    <ArrowBack />
                </IconButton>
                {produto.imagens?.length ? (
                    <Avatar
                        alt={produto.descricao}
                        src={produto.imagens[0].url}
                        sx={{ width: 36, height: 36 }}
                    />
                ) : (
                    <Avatar
                        alt={produto.descricao}
                        src={`${process.env.PUBLIC_URL}/images/produtos/pizza.png`}
                        sx={{ width: 36, height: 36, objectFit: 'contain' }}
                    />
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ ...texto.titulo, lineHeight: 1.35, ...limitarLinhas(2) }}>
                        {produto.descricao}
                    </Typography>
                    <Typography sx={{ fontSize: '13px', fontWeight: 800, color: cores.marca[500], mt: 0.25 }}>
                        {real(valorTotal)}
                    </Typography>
                </Box>
            </Box>

            {/* Form */}
            <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'auto',
                    minHeight: 0,
                }}
            >
                <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
                    {/* Sabores */}
                    <Box>
                        <Stack direction="row" justifyContent="space-between" mb={0.5}>
                            <Typography variant="subtitle2" fontWeight="bold">Sabores<Typography component="span" variant="body2" color="error" sx={{ ml: 0.5 }}>*</Typography></Typography>
                            <Typography variant="subtitle2" fontWeight="bold" color="primary">
                                {sabores.length && valorTotalSabor ? real(valorTotalSabor) : "R$ 0,00"}
                            </Typography>
                        </Stack>
                        {produtoIProduto.sabores && (
                            <Autocomplete
                                multiple
                                value={sabores}
                                noOptionsText="Nenhum sabor encontrado"
                                onChange={handleSaboresChange}
                                options={produtoIProduto.sabores}
                                getOptionLabel={(sabor) => sabor.descricao}
                                renderInput={(params) => (
                                    <TextField {...params} label="Sabores" placeholder="Sabores" size="small" autoFocus />
                                )}
                                isOptionEqualToValue={() => false}
                            />
                        )}
                    </Box>

                    {produtoIProduto.bordas && produtoIProduto.bordas.length > 0 && (
                        <>
                            <Divider />
                            {/* Borda */}
                            <Box>
                                <Stack direction="row" justifyContent="space-between" mb={0.5}>
                                    <Typography variant="subtitle2" fontWeight="bold">
                                        Borda{produtoIProduto.configuracao?.bordaObrigatoria && <Typography component="span" variant="body2" color="error" sx={{ ml: 0.5 }}>*</Typography>}
                                    </Typography>
                                    <Typography variant="subtitle2" fontWeight="bold" color="primary">
                                        {borda ? real(borda.preco) : "R$ 0,00"}
                                    </Typography>
                                </Stack>
                                <Autocomplete
                                    value={borda}
                                    onChange={(_e, value) => setBorda(value)}
                                    noOptionsText="Nenhuma borda encontrada"
                                    options={produtoIProduto.bordas}
                                    getOptionLabel={(b) => b.descricao}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Borda" placeholder="Borda" size="small" />
                                    )}
                                />
                            </Box>
                        </>
                    )}

                    {produtoIProduto.massas && produtoIProduto.massas.length > 0 && (
                        <>
                            <Divider />
                            {/* Massa */}
                            <Box>
                                <Stack direction="row" justifyContent="space-between" mb={0.5}>
                                    <Typography variant="subtitle2" fontWeight="bold">
                                        Massa{produtoIProduto.configuracao?.massaObrigatoria && <Typography component="span" variant="body2" color="error" sx={{ ml: 0.5 }}>*</Typography>}
                                    </Typography>
                                    <Typography variant="subtitle2" fontWeight="bold" color="primary">
                                        {massa ? real(massa.preco) : "R$ 0,00"}
                                    </Typography>
                                </Stack>
                                <Autocomplete
                                    value={massa}
                                    onChange={(_e, value) => setMassa(value)}
                                    noOptionsText="Nenhuma massa encontrada"
                                    options={produtoIProduto.massas}
                                    getOptionLabel={(m) => m.descricao}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Massa" placeholder="Massa" size="small" />
                                    )}
                                />
                            </Box>
                        </>
                    )}

                    {produtoIProduto.adicionais && produtoIProduto.adicionais.length > 0 && (
                        <>
                            <Divider />
                            <Box>
                                <Stack direction="row" justifyContent="space-between" mb={0.5}>
                                    <Typography variant="subtitle2" fontWeight="bold">
                                        Adicionais
                                        {produtoIProduto.configuracao?.maxAdicionais !== undefined && (
                                            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                                ({adicionaisSelecionados.length}/{produtoIProduto.configuracao.maxAdicionais})
                                            </Typography>
                                        )}
                                    </Typography>
                                    <Typography variant="subtitle2" fontWeight="bold" color="primary">
                                        {valorTotalAdicionais ? real(valorTotalAdicionais) : "R$ 0,00"}
                                    </Typography>
                                </Stack>
                                <Stack direction="row" flexWrap="wrap" gap={1}>
                                    {produtoIProduto.adicionais.map(adicional => {
                                        const selecionado = adicionaisSelecionados.some(a => a.uuid === adicional.uuid);
                                        return (
                                            <Chip
                                                key={adicional.uuid}
                                                label={`${adicional.descricao} (+${real(adicional.preco)})`}
                                                onClick={() => handleToggleAdicional(adicional)}
                                                color={selecionado ? "primary" : "default"}
                                                variant={selecionado ? "filled" : "outlined"}
                                                clickable={selecionado || !limiteAdicionaisAtingido}
                                                disabled={!selecionado && limiteAdicionaisAtingido}
                                                size="small"
                                            />
                                        );
                                    })}
                                </Stack>
                            </Box>
                        </>
                    )}

                    <Divider />

                    {/* Observação */}
                    <Box>
                        <Stack direction="row" justifyContent="space-between" mb={0.5}>
                            <Typography variant="subtitle2" fontWeight="bold">Observação</Typography>
                            <Typography variant="subtitle2" fontWeight="bold" color="primary">
                                {valorAdicional ? real(valorAdicional) : "R$ 0,00"}
                            </Typography>
                        </Stack>
                        <Stack gap={1.25}>
                            <TextField
                                fullWidth
                                label="Observação"
                                placeholder="Ex: bem assada, sem orégano..."
                                size="small"
                                multiline
                                maxRows={4}
                                onChange={(e) => setObservacao(e.target.value)}
                                value={observacao ?? ""}
                            />
                            <TextField
                                fullWidth
                                label="Valor adicional"
                                placeholder="Valor adicional"
                                size="small"
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValorAdicional(Number(e.target.value))}
                                value={valorAdicional ?? ""}
                                InputProps={{
                                    inputComponent: RealInput as any,
                                }}
                            />
                        </Stack>
                    </Box>
                </Box>

                <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    sx={theme => ({
                        ...acaoPrincipal(theme),
                        flexShrink: 0,
                        height: 56,
                        borderRadius: 0,
                        fontSize: '13px',
                    })}
                >
                    {isEdicao ? "Salvar alterações" : "Adicionar ao carrinho"}
                </Button>
            </Box>
        </Box>
    );
};
